import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../auth/session';

type Category = { id: number; code: string; name: string; sort_order: number; module_count: number };
type Migration = {
  migration_id: number;
  from_category: string;
  to_category: string;
  phase: string;
  deactivated_modules: string[];
  activated_modules: string[];
  onboarding_reset: boolean;
  completed: boolean;
};

export function CategoryMigrationPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentCategory, setCurrentCategory] = useState<string>('');
  const [targetCategory, setTargetCategory] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Migration | null>(null);
  const [history, setHistory] = useState<Array<Record<string, unknown>>>([]);
  const [confirmStep, setConfirmStep] = useState(false);

  const load = useCallback(async () => {
    try {
      const [catRes, sessRes, histRes] = await Promise.all([
        fetch('/api/v1/auth/catalog/categories').then((r) => r.json()),
        apiFetch('/api/v1/tenant/session').then((r) => r.json()),
        apiFetch('/api/v1/tenant/category/migrations').then((r) => r.json().catch(() => ({ migrations: [] }))),
      ]);
      if (catRes.categories) setCategories(catRes.categories);
      if (sessRes.category_code) setCurrentCategory(sessRes.category_code);
      if (histRes.migrations) setHistory(histRes.migrations);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function doMigration() {
    if (!targetCategory || targetCategory === currentCategory) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiFetch('/api/v1/tenant/category', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ category_code: targetCategory }),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || body.message || `Migration failed (${res.status})`);
      }
      setResult(body.migration);
      setConfirmStep(false);
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const targetCat = categories.find((c) => c.code === targetCategory);
  const willLose = result?.deactivated_modules || [];
  const willGain = result?.activated_modules || [];

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: '1.5rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/tenant/settings" style={{ fontSize: '0.85rem', color: 'var(--sk-muted)' }}>← Impostazioni</Link>
      </div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Cambio categoria</h1>
      <p style={{ color: 'var(--sk-muted)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
        Cambia la categoria del tuo locale. La migrazione disattiva i moduli del vecchio verticale,
        attiva il modulo base del nuovo verticale e resetta l'onboarding.
      </p>

      {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '1rem' }}>{error}</p>}

      {result && (
        <div style={{
          padding: '1rem', marginBottom: '1.5rem', borderRadius: '0.5rem',
          background: '#dcfce7', border: '1px solid #86efac',
        }}>
          <strong style={{ fontSize: '0.9rem' }}>✓ Migrazione completata</strong>
          <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
            Categoria cambiata da <strong>{result.from_category}</strong> a <strong>{result.to_category}</strong>.
            {result.onboarding_reset && ' Onboarding resettato per il nuovo verticale.'}
          </p>
          {willLose.length > 0 && (
            <p style={{ fontSize: '0.8rem', marginTop: '0.5rem', color: '#666' }}>
              Moduli disattivati: {willLose.join(', ')}
            </p>
          )}
          {willGain.length > 0 && (
            <p style={{ fontSize: '0.8rem', color: '#666' }}>
              Moduli attivati: {willGain.join(', ')}
            </p>
          )}
          <Link to="/tenant/home" style={{ fontSize: '0.85rem', display: 'inline-block', marginTop: '0.5rem' }}>
            Riprendi onboarding →
          </Link>
        </div>
      )}

      {/* Current category */}
      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Categoria attuale</h2>
        <div style={{
          padding: '0.75rem 1rem', borderRadius: '0.5rem',
          background: 'var(--sk-bg-alt, #f9fafb)', fontSize: '0.9rem',
        }}>
          <strong style={{ textTransform: 'capitalize' }}>{currentCategory}</strong>
        </div>
      </section>

      {/* Target category selection */}
      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Nuova categoria</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {categories.filter((c) => c.code !== currentCategory).map((cat) => (
            <button
              key={cat.id}
              onClick={() => { setTargetCategory(cat.code); setConfirmStep(false); }}
              disabled={busy}
              style={{
                padding: '0.75rem 1.25rem', borderRadius: '0.5rem', border: '1px solid var(--sk-border)',
                background: targetCategory === cat.code ? '#3b82f6' : 'transparent',
                color: targetCategory === cat.code ? '#fff' : '#000',
                cursor: 'pointer', fontSize: '0.9rem',
              }}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </section>

      {/* Checklist before migration */}
      {targetCategory && !confirmStep && (
        <section style={{
          marginBottom: '1.5rem', padding: '1rem', borderRadius: '0.5rem',
          background: '#fef3c7', border: '1px solid #fcd34d',
        }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginTop: 0, marginBottom: '0.5rem' }}>
            ⚠️ Cosa succederà
          </h3>
          <ul style={{ fontSize: '0.85rem', margin: 0, paddingLeft: '1.25rem', color: '#666' }}>
            <li>Tutti i moduli <strong>{currentCategory}</strong> verranno disattivati</li>
            <li>Il modulo base <strong>{targetCategory}-base</strong> verrà attivato</li>
            <li>L'onboarding verrà resettato per il nuovo verticale</li>
            <li>I dati esistenti (menu, prenotazioni, ecc.) NON verranno eliminati</li>
            <li>Se la verifica fallisce, il sistema farà rollback automatico</li>
          </ul>
          <button
            onClick={() => setConfirmStep(true)}
            disabled={busy}
            style={{
              marginTop: '0.75rem', padding: '0.5rem 1rem', borderRadius: '0.5rem',
              background: '#f59e0b', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
            }}
          >
            Procedi →
          </button>
        </section>
      )}

      {/* Confirmation */}
      {targetCategory && confirmStep && (
        <section style={{
          marginBottom: '1.5rem', padding: '1rem', borderRadius: '0.5rem',
          background: '#fee2e2', border: '1px solid #fca5a5',
        }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginTop: 0, marginBottom: '0.5rem' }}>
            Conferma migrazione
          </h3>
          <p style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            Sei sicuro di voler cambiare da <strong>{currentCategory}</strong> a{' '}
            <strong>{targetCat?.name || targetCategory}</strong>? Questa operazione non può essere annullata
            (ma puoi tornare indietro con una nuova migrazione).
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => doMigration()}
              disabled={busy}
              style={{
                padding: '0.5rem 1rem', borderRadius: '0.5rem',
                background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              {busy ? 'Migrazione in corso…' : 'Conferma e migra'}
            </button>
            <button
              onClick={() => setConfirmStep(false)}
              disabled={busy}
              style={{
                padding: '0.5rem 1rem', borderRadius: '0.5rem',
                background: 'transparent', border: '1px solid var(--sk-border)', cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              Annulla
            </button>
          </div>
        </section>
      )}

      {/* Migration history */}
      {history.length > 0 && (
        <section style={{ marginTop: '2rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Storico migrazioni</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {history.map((m, i) => (
              <div key={i} style={{
                padding: '0.6rem 0.75rem', borderRadius: '0.5rem',
                background: 'var(--sk-bg-alt, #f9fafb)', fontSize: '0.8rem',
              }}>
                <span style={{ fontWeight: 600 }}>
                  {String(m.from_category_code)} → {String(m.to_category_code)}
                </span>
                <span style={{ marginLeft: '0.5rem', color: m.phase === 'completed' ? '#16a34a' : m.phase === 'rolled_back' ? '#dc2626' : '#666' }}>
                  {String(m.phase)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default CategoryMigrationPage;
